import { useNavigate, useLocation } from 'react-router-dom'
import { useAdmin } from '../context/AdminContext'
import { useState } from 'react'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const RED    = '#DC2626'

const ROLE_STYLES = {
  super_admin:     { bg: '#FEF2F2', color: RED },
  admin:           { bg: '#EFF6FF', color: BLUE },
  support:         { bg: '#F0FDF4', color: '#16A34A' },
  finance:         { bg: '#FFFBEB', color: '#D97706' },
  marketing:       { bg: '#F5F3FF', color: '#7C3AED' },
  it:              { bg: BG,        color: MUTED },
  account_manager: { bg: '#F0FDFA', color: '#0F766E' },
}

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard',  path: '/admin/dashboard',     icon: '📊' },
    ],
  },
  {
    label: 'Sellers',
    items: [
      { label: 'Sellers',      path: '/admin/sellers',       icon: '🏪' },
      { label: 'Verify',       path: '/admin/verifications', icon: '✅' },
      { label: 'Help',         path: '/admin/help',          icon: '🆘' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Analytics',  path: '/admin/analytics',  icon: '📈', financeOnly: true },
      { label: 'Finance',    path: '/admin/finance',    icon: '💰', financeOnly: true },
      { label: 'Referrals',  path: '/admin/referrals',  icon: '🎁', financeOnly: true },
      { label: 'Goals',      path: '/admin/goals',      icon: '🎯', financeOnly: true },
    ],
  },
  {
    label: 'Platform',
    items: [
      { label: 'AdBoost',   path: '/admin/adboost',  icon: '📢', adminOnly: true },
      { label: 'Ad Slots',  path: '/admin/ads',      icon: '🪧', adminOnly: true },
      { label: 'Flags',     path: '/admin/flags',    icon: '⚑',  adminOnly: true },
      { label: 'Users',     path: '/admin/users',    icon: '👥', superOnly: true },
      { label: 'Settings',  path: '/admin/settings', icon: '⚙️', superOnly: true },
    ],
  },
]

const ICONS = {
  collapse: 'M11 19l-7-7 7-7m8 14l-7-7 7-7',
  expand:   'M13 5l7 7-7 7M5 5l7 7-7 7',
  logout:   'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
}

function NavIcon({ d, size = 14, color = MUTED }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function AdminNav() {
  const { admin, signOut } = useAdmin()
  const navigate  = useNavigate()
  const location  = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleSignOut = () => { signOut(); navigate('/admin') }

  const isSuperOrAdmin = admin?.role === 'super_admin' || admin?.role === 'admin'
  const isFinanceRole  = isSuperOrAdmin || admin?.role === 'finance'

  const isVisible = (item) => {
    if (item.superOnly)   return isSuperOrAdmin
    if (item.adminOnly)   return isSuperOrAdmin || admin?.role === 'marketing'
    if (item.financeOnly) return isFinanceRole
    return true
  }

  const isActive = (path) => location.pathname === path
  const roleStyle = ROLE_STYLES[admin?.role] || { bg: BG, color: MUTED }
  const initials = (admin?.name || 'AD').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside style={{
        width: collapsed ? '60px' : '210px',
        background: WHITE,
        borderRight: '1px solid ' + BORDER,
        display: 'flex', flexDirection: 'column',
        height: '100vh', position: 'sticky', top: 0,
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflowX: 'hidden', flexShrink: 0,
        fontFamily: 'Space Grotesk, system-ui, sans-serif',
        zIndex: 40,
      }} className="pc-admin-sidebar">

        {/* Logo */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: collapsed ? '14px 0' : '14px 16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid ' + BORDER, minHeight: '56px', flexShrink: 0,
        }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: '14px' }}>⚡</span>
          </div>
          {!collapsed && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px', lineHeight: 1.1 }}>PageConnect</div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: RED, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Admin</div>
            </div>
          )}
        </div>

        {/* Admin pill */}
        {!collapsed && (
          <div style={{ margin: '10px 10px 4px', padding: '9px 11px', borderRadius: '10px', background: BG, display: 'flex', alignItems: 'center', gap: '9px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: RED, color: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin?.name || 'Admin'}</div>
              <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '20px', background: roleStyle.bg, color: roleStyle.color }}>
                {admin?.role?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        )}

        {/* Nav groups */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '8px 6px' : '6px 8px', scrollbarWidth: 'none' }}>
          {NAV_GROUPS.map(group => {
            const visibleItems = group.items.filter(isVisible)
            if (!visibleItems.length) return null
            return (
              <div key={group.label} style={{ marginBottom: '4px' }}>
                {!collapsed && (
                  <div style={{ padding: '8px 8px 3px', fontSize: '9.5px', fontWeight: 700, color: '#B0B3B8', letterSpacing: '0.09em', textTransform: 'uppercase' }}>
                    {group.label}
                  </div>
                )}
                {visibleItems.map(item => (
                  <button key={item.path}
                    onClick={() => navigate(item.path)}
                    title={collapsed ? item.label : ''}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center',
                      gap: '9px', padding: collapsed ? '9px 0' : '7px 9px',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderRadius: '8px', marginBottom: '1px',
                      background: isActive(item.path) ? '#FEF2F2' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      transition: 'background 0.12s', position: 'relative',
                      fontFamily: 'Space Grotesk, system-ui, sans-serif',
                    }}
                    onMouseEnter={e => { if (!isActive(item.path)) e.currentTarget.style.background = BG }}
                    onMouseLeave={e => { if (!isActive(item.path)) e.currentTarget.style.background = 'transparent' }}>
                    <span style={{ fontSize: '15px', flexShrink: 0 }}>{item.icon}</span>
                    {!collapsed && (
                      <span style={{ fontSize: '13px', fontWeight: isActive(item.path) ? 700 : 500, color: isActive(item.path) ? RED : TEXT, flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label}
                      </span>
                    )}
                    {collapsed && (
                      <div className="admin-tooltip" style={{ position: 'absolute', left: 'calc(100% + 10px)', top: '50%', transform: 'translateY(-50%)', background: TEXT, color: WHITE, fontSize: '11.5px', fontWeight: 500, padding: '5px 10px', borderRadius: '7px', whiteSpace: 'nowrap', pointerEvents: 'none', opacity: 0, zIndex: 100, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                        {item.label}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )
          })}
        </nav>

        {/* Bottom */}
        <div style={{ flexShrink: 0, borderTop: '1px solid ' + BORDER, padding: collapsed ? '8px 6px' : '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <button onClick={() => setCollapsed(!collapsed)}
            style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: collapsed ? '8px 0' : '7px 9px', justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', transition: 'background 0.12s', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}
            onMouseEnter={e => e.currentTarget.style.background = BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <NavIcon d={collapsed ? ICONS.expand : ICONS.collapse} color={MUTED} />
            {!collapsed && <span style={{ fontSize: '13px', color: MUTED }}>Collapse</span>}
          </button>
          <button onClick={handleSignOut}
            style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: collapsed ? '8px 0' : '7px 9px', justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: '8px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', transition: 'background 0.12s', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}
            onMouseEnter={e => e.currentTarget.style.background = '#FFF0F0'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <NavIcon d={ICONS.logout} color={RED} />
            {!collapsed && <span style={{ fontSize: '13px', color: RED, fontWeight: 500 }}>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* ── MOBILE TOP BAR ── */}
      <div style={{ background: WHITE, borderBottom: '1px solid ' + BORDER, position: 'sticky', top: 0, zIndex: 100, fontFamily: 'Space Grotesk, system-ui, sans-serif' }} className="pc-admin-topbar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
          <button onClick={() => navigate('/admin/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '14px' }}>⚡</span>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: TEXT, lineHeight: 1 }}>PageConnect</div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: RED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Admin</div>
            </div>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: roleStyle.bg, color: roleStyle.color }}>
              {admin?.role?.replace(/_/g, ' ')}
            </span>
            <button onClick={() => setMobileOpen(!mobileOpen)}
              style={{ width: '36px', height: '36px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {mobileOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div style={{ borderTop: '1px solid ' + BORDER, padding: '12px 16px', background: WHITE }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {NAV_GROUPS.flatMap(g => g.items).filter(isVisible).map(item => (
                <button key={item.path}
                  onClick={() => { navigate(item.path); setMobileOpen(false) }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '10px 6px', borderRadius: '12px', background: isActive(item.path) ? '#FEF2F2' : BG, border: 'none', cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                  <span style={{ fontSize: '18px' }}>{item.icon}</span>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: isActive(item.path) ? RED : TEXT, textAlign: 'center', lineHeight: 1.2 }}>{item.label}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid ' + BORDER }}>
              <div style={{ fontSize: '12px', color: MUTED }}>Signed in as <strong style={{ color: TEXT }}>{admin?.name}</strong></div>
              <button onClick={handleSignOut} style={{ fontSize: '12px', fontWeight: 600, color: RED, background: '#FEF2F2', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                Sign out
              </button>
            </div>
          </div>
        )}

        {!mobileOpen && (
          <div style={{ display: 'flex', overflowX: 'auto', gap: '4px', padding: '0 16px 10px', scrollbarWidth: 'none' }}>
            {NAV_GROUPS.flatMap(g => g.items).filter(isVisible).map(item => (
              <button key={item.path} onClick={() => navigate(item.path)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', background: isActive(item.path) ? '#FEF2F2' : BG, border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: isActive(item.path) ? RED : MUTED, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .pc-admin-sidebar  { display: flex !important; }
        .pc-admin-topbar   { display: none !important; }
        @media(max-width: 768px) {
          .pc-admin-sidebar { display: none !important; }
          .pc-admin-topbar  { display: block !important; }
        }
        .pc-admin-sidebar nav::-webkit-scrollbar { display: none; }
        button:hover .admin-tooltip { opacity: 1 !important; }
      `}</style>
    </>
  )
}

export default AdminNav